import React, { useMemo, useEffect } from "react";
import { Line } from "@react-three/drei";
import * as THREE from "three";
import GridResults3D from "./GridResults3D";

const Grid3D = ({ grid, areaResults, state, gridScaleX, gridScaleY, minResult, type, camera, renderOrder = 0 }) => {
  const [gridResults, setGridResults] = React.useState([]);

  const borderPoints = useMemo(() => {
    const { origin } = grid;
    if (!origin) return [];

    // console.log(type, "Grid origin", grid);

    const width = Number(origin.x.length);
    const height = Number(origin.y?.length ?? origin.z?.length ?? 0);
    const scaleX = grid.scaleX ?? 1;
    const scaleY = grid.scaleY ?? 1;
    const rotationDeg = grid.angle ?? 0;
    const rotationRad = -(rotationDeg * Math.PI) / 180;

    if (type === "Horizontal") {
      const x = Number(origin.x.start);
      const y = Number(origin.y.start) * -1;
      // StageCanvas rotates the overlay group on X, so:
      // negative local Z => above the DXF plane
      // positive local Z => below the DXF plane
      let z = -0.08;
      if (grid.name === "Grid #0") z = 10;

      const anchor = new THREE.Vector2(x, y);

      const corners = [
        new THREE.Vector2(x, y),
        new THREE.Vector2(x + width, y),
        new THREE.Vector2(x + width, y - height),
        new THREE.Vector2(x, y - height),
      ];

      const transformed = corners.map((p) => {
        const translated = new THREE.Vector2().subVectors(p, anchor);
        translated.x *= scaleX;
        translated.y *= scaleY;

        const rotated = new THREE.Vector2(
          translated.x * Math.cos(rotationRad) - translated.y * Math.sin(rotationRad),
          translated.x * Math.sin(rotationRad) + translated.y * Math.cos(rotationRad)
        );

        return rotated.add(anchor);
      });

      transformed.push(transformed[0]);

      // console.log("3D Horizontal grid points", transformed.map(p => [p.x, p.y, z]));
      return transformed.map((p) => [p.x, p.y, z]);
    }

    if (type === "Vertical") {
      const x = Number(origin.x.start);
      const y = Number(origin.y.start) * -1; // depth
      const zStart = -Number(origin.z.start);
      const zHeight = Number(origin.z.length);
      const zTop = zStart - zHeight;

      // We'll hinge on the left edge
      const hingeX = x;
      const hingeY = y;

      // Rectangle in X–Z plane, at constant Y before rotation
      const corners = [
        new THREE.Vector3(x, y, zStart),               // bottom hinge
        new THREE.Vector3(x + width, y, zStart),       // bottom far
        new THREE.Vector3(x + width, y, zTop), // top far
        new THREE.Vector3(x, y, zTop),     // top hinge
      ];

      // Rotate around Z-axis (vertical hinge)
      const transformed = corners.map((p) => {
        const localX = p.x - hingeX;
        const localY = p.y - hingeY;

        const rotatedX = localX * Math.cos(rotationRad) - localY * Math.sin(rotationRad);
        const rotatedY = localX * Math.sin(rotationRad) + localY * Math.cos(rotationRad);

        return new THREE.Vector3(rotatedX + hingeX, rotatedY + hingeY, p.z);
      });

      transformed.push(transformed[0]);

      // console.log("3D Vertical grid points", transformed.map((p) => [p.x, p.y, p.z]));

      return transformed.map((p) => [p.x, p.y, p.z]);
    }


  }, [grid, type]);

  useEffect(() => {
    if (!areaResults) return;
    for (let i = 0; i < areaResults.length; i++) {
      const item = areaResults[i];
      if (item.calcResults?.outdoorResults[0]?.gridId === grid.id) {
        setGridResults(item.calcResults?.outdoorResults[0]?.contourResults);
        break;
      }
    }
  }, [areaResults, grid.id]);

  // console.log(grid.showValues)

  return (
    <>
      {borderPoints && grid.name !== "Grid #0" && (
        <Line
          points={borderPoints}
          color={grid.color || "blue"}
          lineWidth={3}
          opacity={grid.opacity}
          renderOrder={renderOrder}
          depthTest
          depthWrite={false}
        />
      )}
      {gridResults?.length > 0 && (
        <GridResults3D
          grid={grid}
          results={gridResults}
          showValues={grid.showValues ?? true}
          minResult={minResult}
          type={type}
          camera={camera}
        />
      )}
    </>
  );
};

export default Grid3D;
